/**
 * Slide thumbnail bar with support for vertical (sidebar) and horizontal (bottom) layouts.
 * Renders scaled-down versions of actual slides for rich presentations.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DOMPurify from 'dompurify';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { useSlideDragAndDrop } from '../hooks/useSlideDragAndDrop';
import { serializeSlidesToHTML } from '../parser';
function SlideThumbnail({ slide, index, isActive, isDragging, isDraggedOver, onClick, onDelete, onDragStart, onDragOver, onDragEnd, onDrop, totalSlides, orientation, richSectionHTML, styleTag }) {
    const isRich = Boolean(richSectionHTML);
    const canDrag = Boolean(onDelete); // If we can delete, we can drag (simple presentations)
    // Dimensions based on orientation
    const dimensions = orientation === 'vertical' ? 'w-full h-32' // Vertical: full width, fixed height
     : 'w-36 h-24'; // Horizontal: fixed width, shorter height
    // Generate slide HTML for thumbnail preview
    const thumbnailHTML = isRich ? (styleTag || '') + richSectionHTML : serializeSlidesToHTML([
        slide
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        draggable: canDrag,
        onDragStart: canDrag ? (e)=>onDragStart(e, index) : undefined,
        onDragOver: canDrag ? (e)=>onDragOver(e, index) : undefined,
        onDragEnd: canDrag ? onDragEnd : undefined,
        onDrop: canDrag ? (e)=>onDrop(e, index) : undefined,
        className: `
        relative flex-shrink-0 ${dimensions} rounded-lg border-2 cursor-pointer transition-all overflow-hidden
        ${isDragging ? 'opacity-50 scale-95' : ''}
        ${isDraggedOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 scale-105' : ''}
        ${isActive && !isDraggedOver ? 'border-blue-500 ring-2 ring-blue-500/30' : ''}
        ${!isActive && !isDraggedOver ? 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500' : ''}
      `,
        onClick: onClick,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "reveal absolute inset-0 origin-top-left bg-white",
                style: {
                    transform: 'scale(0.15)',
                    width: '666%',
                    height: '666%'
                },
                children: /*#__PURE__*/ _jsx("div", {
                    className: "slides w-full h-full",
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: Content sanitized
                    dangerouslySetInnerHTML: {
                        __html: DOMPurify.sanitize(thumbnailHTML, {
                            ADD_TAGS: [
                                'section',
                                'style'
                            ],
                            ADD_ATTR: [
                                'data-theme',
                                'class'
                            ],
                            FORCE_BODY: true
                        })
                    }
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "absolute inset-0 bg-transparent hover:bg-black/5 transition-colors"
            }),
            canDrag && /*#__PURE__*/ _jsx("div", {
                className: "absolute top-1 left-1 p-0.5 cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 bg-white/80 dark:bg-gray-800/80 rounded",
                title: "Drag to reorder",
                children: /*#__PURE__*/ _jsx(GripVertical, {
                    className: "w-3 h-3"
                })
            }),
            onDelete && totalSlides > 1 && /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: (e)=>{
                    e.stopPropagation();
                    onDelete();
                },
                className: "absolute top-1 right-1 p-0.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 bg-white/80 dark:bg-gray-800/80 rounded transition-colors",
                "aria-label": "Delete slide",
                children: /*#__PURE__*/ _jsx(Trash2, {
                    className: "w-3 h-3"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "absolute bottom-1 left-1 px-1.5 py-0.5 bg-gray-900/80 text-white text-[10px] font-medium rounded",
                children: index + 1
            })
        ]
    });
}
export function SlideThumbnailBar({ slides, currentSlideIndex, onSlideClick, onSlideReorder, onSlideDelete, onAddSlide, orientation, richSections, styleTag }) {
    const { draggedIndex, draggedOverIndex, handleDragStart, handleDragOver, handleDrop: handleDropInternal, handleDragEnd } = useSlideDragAndDrop();
    const handleDrop = (e, dropIndex)=>{
        if (onSlideReorder) {
            handleDropInternal(e, dropIndex, onSlideReorder);
        }
    };
    const isVertical = orientation === 'vertical';
    return /*#__PURE__*/ _jsxs("div", {
        className: `
        ${isVertical ? 'w-48 max-h-screen flex-col border-r overflow-y-auto' : 'w-full h-32 flex-row border-t overflow-x-auto'}
        flex gap-2 p-3 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700
      `,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: `flex ${isVertical ? 'flex-col' : 'flex-row'} gap-2`,
                children: slides.map((slide, index)=>/*#__PURE__*/ _jsx(SlideThumbnail, {
                        slide: slide,
                        index: index,
                        isActive: index === currentSlideIndex,
                        isDragging: draggedIndex === index,
                        isDraggedOver: draggedOverIndex === index,
                        onClick: ()=>onSlideClick(index),
                        onDelete: onSlideDelete ? ()=>onSlideDelete(index) : undefined,
                        onDragStart: handleDragStart,
                        onDragOver: handleDragOver,
                        onDragEnd: handleDragEnd,
                        onDrop: handleDrop,
                        totalSlides: slides.length,
                        orientation: orientation,
                        richSectionHTML: richSections?.[index],
                        styleTag: styleTag
                    }, slide.id))
            }),
            onAddSlide && /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: onAddSlide,
                className: `
            flex-shrink-0 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600
            hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700
            transition-colors flex flex-col items-center justify-center gap-1 text-gray-500 dark:text-gray-400
            ${isVertical ? 'w-full h-24' : 'w-36 h-full'}
          `,
                children: [
                    /*#__PURE__*/ _jsx(Plus, {
                        className: "w-5 h-5"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs",
                        children: "Add Slide"
                    })
                ]
            })
        ]
    });
}
